import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { useGeolocation, formatCoordinates, getGoogleMapsUrl } from '../../hooks/useGeolocation';
import { queueServiceLog } from '../../services/offline-queue';
import { captureFromCamera, compressImage } from '../../services/image-upload';
import { syncPendingLogs, isOnline } from '../../services/sync-service';

// =============================================================================
// TYPES
// =============================================================================

export interface ServiceLogFormProps {
  employeeId: string;
  employeeName: string;
  clientId: string;
  clientName: string;
  onSuccess: () => void;
  onCancel: () => void;
}

type MissedServiceType = null | 'Missed Service' | 'Partial Missed';

// =============================================================================
// COMPONENT
// =============================================================================

export function ServiceLogForm({
  employeeId,
  employeeName,
  clientId,
  clientName,
  onSuccess,
  onCancel,
}: ServiceLogFormProps) {
  const [missedService, setMissedService] = useState<MissedServiceType>(null);
  const [serviceNotes, setServiceNotes] = useState('');
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    position,
    error: geoError,
    loading: geoLoading,
    getPosition,
  } = useGeolocation({ autoStart: true });

  // Format current time
  const now = new Date();
  const timeFormatted = now.toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const dateFormatted = now.toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  // Generate visit log preview
  const firstName = employeeName.split(' ')[0];
  const visitLogPreview = `${firstName} ${now.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short' })} ${now.getMonth() + 1}/${now.getDate()} ${timeFormatted} ${clientId}`;

  // Handle image capture
  const handleCaptureImage = async () => {
    try {
      const file = await captureFromCamera();
      if (file) {
        // Compress the image
        const compressed = await compressImage(file, 1920, 0.8);
        setImageBlob(compressed);

        // Create preview URL
        const previewUrl = URL.createObjectURL(compressed);
        setImagePreview(previewUrl);
      }
    } catch (err) {
      setError('Failed to capture image');
    }
  };

  // Remove captured image
  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageBlob(null);
    setImagePreview(null);
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!position) {
      setError('GPS position required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Queue the service log (works offline)
      await queueServiceLog({
        employeeId,
        employeeName,
        clientId,
        clientName,
        latitude: position.latitude,
        longitude: position.longitude,
        missedService: missedService,
        serviceNotes: serviceNotes || null,
        imageBlob: imageBlob || undefined,
      });

      // Try to sync immediately if online
      if (isOnline()) {
        await syncPendingLogs();
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service log');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:w-[480px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up">
        {/* Header */}
        <div className="bg-forest-800 text-white px-4 py-4 safe-top">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-forest-200 text-sm">Log Service</p>
              <h2 className="text-lg font-semibold">{clientName}</h2>
            </div>
            <button
              onClick={onCancel}
              className="w-10 h-10 rounded-full bg-forest-700/50 flex items-center justify-center"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Read-only fields */}
          <Card>
            <CardContent className="py-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-sage-500 text-xs">Technician</p>
                  <p className="font-medium text-forest-900">{employeeName}</p>
                </div>
                <div>
                  <p className="text-sage-500 text-xs">Date & Time</p>
                  <p className="font-medium text-forest-900">{dateFormatted} {timeFormatted}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visit Log Preview */}
          <Card>
            <CardContent className="py-3">
              <p className="text-sage-500 text-xs mb-1">Visit Log</p>
              <p className="font-mono text-sm text-forest-800">{visitLogPreview}</p>
            </CardContent>
          </Card>

          {/* GPS Status */}
          <Card>
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sage-500 text-xs mb-1">Location</p>
                  {geoLoading && (
                    <p className="text-sm text-sage-600">Getting location...</p>
                  )}
                  {geoError && (
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-earth-600">
                        {geoError === 'PERMISSION_DENIED'
                          ? 'Location access denied'
                          : geoError === 'NOT_SUPPORTED'
                          ? 'GPS not supported'
                          : 'Could not get location'}
                      </p>
                      <Button size="sm" variant="ghost" onClick={() => getPosition()}>
                        Retry
                      </Button>
                    </div>
                  )}
                  {position && (
                    <a
                      href={getGoogleMapsUrl(position.latitude, position.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:underline"
                    >
                      {formatCoordinates(position.latitude, position.longitude)}
                    </a>
                  )}
                </div>
                {position && (
                  <Badge variant="success" size="sm">
                    GPS OK
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Missed Service Toggle */}
          <div>
            <p className="text-sm font-medium text-forest-900 mb-2">Service Status</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMissedService(null)}
                className={`flex-1 py-2.5 px-3 rounded-xl border-2 transition-colors ${
                  missedService === null
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-sage-200 text-sage-600 hover:border-sage-300'
                }`}
              >
                Completed
              </button>
              <button
                type="button"
                onClick={() => setMissedService('Partial Missed')}
                className={`flex-1 py-2.5 px-3 rounded-xl border-2 transition-colors ${
                  missedService === 'Partial Missed'
                    ? 'border-earth-400 bg-earth-50 text-earth-600'
                    : 'border-sage-200 text-sage-600 hover:border-sage-300'
                }`}
              >
                Partial
              </button>
              <button
                type="button"
                onClick={() => setMissedService('Missed Service')}
                className={`flex-1 py-2.5 px-3 rounded-xl border-2 transition-colors ${
                  missedService === 'Missed Service'
                    ? 'border-earth-500 bg-earth-50 text-earth-700'
                    : 'border-sage-200 text-sage-600 hover:border-sage-300'
                }`}
              >
                Missed
              </button>
            </div>
          </div>

          {/* Service Notes */}
          <div>
            <label className="text-sm font-medium text-forest-900 mb-2 block">
              Service Notes <span className="text-sage-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={serviceNotes}
              onChange={(e) => setServiceNotes(e.target.value)}
              placeholder="Add notes about the service..."
              className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none resize-none"
              rows={3}
            />
          </div>

          {/* Image Capture */}
          <div>
            <p className="text-sm font-medium text-forest-900 mb-2">
              Photo <span className="text-sage-400 font-normal">(optional)</span>
            </p>
            {!imagePreview ? (
              <button
                type="button"
                onClick={handleCaptureImage}
                className="w-full py-4 rounded-xl border-2 border-dashed border-sage-300 text-sage-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
              >
                <span className="text-2xl">📷</span>
                <span>Take Photo</span>
              </button>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Service log photo"
                  className="w-full rounded-xl object-cover max-h-48"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-earth-50 border border-earth-200 rounded-xl p-3">
              <p className="text-sm text-earth-700">{error}</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-sage-200 px-4 py-4 safe-bottom flex gap-3">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleSubmit}
            disabled={isSubmitting || !position}
          >
            {isSubmitting ? 'Saving...' : 'Log Service'}
          </Button>
        </div>
      </div>
    </div>
  );
}
